import { camelCase } from 'lodash/string';
import type { UserRegistrationFields } from 'types/registration';
import { useTranslation } from 'utils/i18n';
import { useUserDetailsFormHook } from '../../../hooks/useUserDetailsFormHook';
import type { FieldChangeEvent } from '../../../types';
import Checkbox from '../../fields/checkbox';
import InputError from '../../fields/inputError';
import PasswordWithStrengthIndicator from '../../fields/passwordWithStrengthIndicator';
import Textbox from '../../fields/textbox';
// Components
import InstructionalFormActions from '../../instructionalForm/actions';

interface Props {
  fields: UserRegistrationFields;
  handleFieldChange: (event: FieldChangeEvent) => void;
  handleSubmitCallback?: () => void;
}

const UserDetails = ({
  fields,
  handleFieldChange,
  handleSubmitCallback = () => null,
}: Props) => {
  const { t } = useTranslation('common');
  const {
    errors,
    handleChange,
    handleSubmit,
    handlePasswordChange,
    marketingOptions,
    isValid,
  } = useUserDetailsFormHook({
    fields,
    handleFieldChange,
    handleSubmitCallback,
    validateEmailUniqueness: true,
  });

  // The camelCased i18n keys map 1:1 onto the string fields of the form.
  const fieldValue = (name: string) =>
    String(fields[camelCase(name) as keyof UserRegistrationFields] ?? '');

  const renderTextboxField = (name: string) => (
    <Textbox
      label={t(`registration.user_details.fields.${name}.label`)}
      name={camelCase(name)}
      value={fieldValue(name)}
      placeholder={t(`registration.user_details.fields.${name}.label`)}
      error={!!errors[camelCase(name)]}
      errorMsg={errors[camelCase(name)]}
      onChange={handleChange}
    />
  );

  return (
    <form
      className="instructional-form__form"
      onSubmit={handleSubmit}
      data-testid="userDetailsForm"
      data-cy="userDetailsForm"
    >
      <h2 data-cy="registrationHeader">
        {t('registration.user_details.title')}
      </h2>
      <div className="field-multicol">
        {renderTextboxField('first_name')}
        {renderTextboxField('last_name')}
      </div>
      <div className="field-multicol">
        {renderTextboxField('phone_number')}
        {renderTextboxField('mobile_number')}
      </div>
      <div className="field-multicol">
        {renderTextboxField('email')}
        {renderTextboxField('confirm_email')}
      </div>
      <div className="field-multicol">
        <div className="field">
          <PasswordWithStrengthIndicator
            value={fields.password}
            onChange={handlePasswordChange}
          />
        </div>
        <Textbox
          label={t('registration.user_details.fields.confirm_password.label')}
          name="confirmPassword"
          type="password"
          value={fields.confirmPassword}
          placeholder={t(
            'registration.user_details.fields.confirm_password.label'
          )}
          error={!!errors.confirmPassword}
          errorMsg={errors.confirmPassword}
          onChange={handleChange}
        />
      </div>

      {!!errors.password && (
        <p className="field password-note-field">
          <InputError errorMsg={errors.password} />
        </p>
      )}

      {marketingOptions.length > 0 && (
        <>
          <p>{t('registration.user_details.marketing_options')}</p>
          <div className="instructional-form__checkboxes">
            {marketingOptions.map(option => (
              <div key={`marketingOptions${option.id}`}>
                <Checkbox
                  label={t('registration.user_details.contact_by', {
                    name: option.name,
                  })}
                  name="marketingOptions[]"
                  id={`marketingOptions${option.id}`}
                  checked={
                    fields.marketingOptions.indexOf(option.id.toString()) !== -1
                  }
                  value={option.id}
                  onChange={handleChange}
                />
              </div>
            ))}
          </div>
        </>
      )}

      <InstructionalFormActions isValid={isValid} showBackBtn={false} />
    </form>
  );
};

export default UserDetails;
